import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Modal from "@commons/Modal";
import { usePage } from "@hooks/usePage";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import { config } from "@/config";
import UploadInput from "@commons/Input/Upload";
import { globalMutate } from "@utils/swr";
import { createAttachmentsCameraTrap } from "@services/device/cameraTrap/createAttachments";

const attachmentsCameraTrapSchema = z.object({
  attachments: z
    .array(z.instanceof(File))
    .min(1, "Selecione pelo menos um arquivo")
    .max(
      config.uploadArchive.maxFiles,
      `Você pode enviar no máximo ${config.uploadArchive.maxFiles} arquivos.`,
    ),
});

type AttachmentsCameraTrapFormData = z.infer<
  typeof attachmentsCameraTrapSchema
>;

function ModalNewAttachmentsCameraTrap() {
  const { isModalOpen, closeModal, dataModal, setIsLoading } = usePage();
  const open = isModalOpen("addCameraTrapAttachments");

  const cameraTrapId = dataModal?.cameraTrapId as string;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AttachmentsCameraTrapFormData>({
    resolver: zodResolver(attachmentsCameraTrapSchema),
    defaultValues: {
      attachments: [],
    },
  });

  const onSubmit = async (data: AttachmentsCameraTrapFormData) => {
    try {
      setIsLoading(true);

      await createAttachmentsCameraTrap({
        id: cameraTrapId,
        files: data.attachments,
      });

      toast.success("Anexos adicionados com sucesso");
      await globalMutate([`fetch-camera-trap-attachments-${cameraTrapId}`]);
      handleClose();
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError ? error.message : "Erro ao adicionar anexos";
      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    closeModal();
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      heading="Adicionar Anexos à Câmera Trap"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-md"
    >
      <form className="flex flex-col gap-4">
        <Controller
          name="attachments"
          control={control}
          render={({ field: { value, onChange, onBlur, name } }) => (
            <UploadInput
              value={value || []}
              onChange={onChange}
              onBlur={onBlur}
              name={name}
              multiple={config.uploadArchive.multiple}
              accept={config.uploadArchive.accept}
              maxFiles={config.uploadArchive.maxFiles}
            />
          )}
        />
        {errors.attachments && (
          <p className="text-red-500 text-sm mt-1">
            {errors.attachments.message}
          </p>
        )}
      </form>
    </Modal>
  );
}

export default ModalNewAttachmentsCameraTrap;
